import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobileShell } from '../../hooks/useMediaQuery';
import { toInertiaUrl } from '../../lib/toInertiaUrl';
import { cn } from '../ui/utils';
import { buildPaginationRange } from './paginationRange';

type PaginatorLink = {
    url: string | null;
    label: string;
    active: boolean;
};

type PaginationProps = {
    from?: number | null;
    to?: number | null;
    total: number;
    lastPage: number;
    links: PaginatorLink[];
    onNavigate: (url: string | null) => void;
    className?: string;
    label?: string;
};

function stripHtml(label: string): string {
    return label
        .replace(/&laquo;/g, '«')
        .replace(/&raquo;/g, '»')
        .replace(/<[^>]*>/g, '');
}

function isNavLink(label: string): boolean {
    const text = stripHtml(label).toLowerCase();
    return (
        text === '«' ||
        text === '»' ||
        text.includes('previous') ||
        text.includes('next')
    );
}

function parsePageNumber(label: string): number | null {
    const text = stripHtml(label).trim();
    if (!/^\d+$/.test(text)) {
        return null;
    }
    return Number(text);
}

function resolvePageUrl(
    page: number,
    pageLinkMap: Map<number, PaginatorLink>,
    links: PaginatorLink[],
): string | null {
    const existing = pageLinkMap.get(page);
    if (existing?.url) {
        return existing.url;
    }

    const template = links.find((link) => {
        if (!link.url) {
            return false;
        }
        return parsePageNumber(link.label) !== null;
    });

    if (!template?.url) {
        return null;
    }

    try {
        const origin =
            typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        const parsed = new URL(template.url, origin);
        parsed.searchParams.set('page', String(page));
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
        return null;
    }
}

function Pagination({
    from,
    to,
    total,
    lastPage,
    links,
    onNavigate,
    className,
    label = 'Paginação',
}: PaginationProps) {
    const isMobile = useIsMobileShell();

    const pageLinkMap = new Map<number, PaginatorLink>();
    let currentPage = 1;

    for (const link of links) {
        if (isNavLink(link.label)) {
            continue;
        }

        const page = parsePageNumber(link.label);
        if (page === null) {
            continue;
        }

        pageLinkMap.set(page, link);
        if (link.active) {
            currentPage = page;
        }
    }

    const prevLink = links.find((link) => {
        const text = stripHtml(link.label).toLowerCase();
        return text.includes('previous') || text === '«';
    });
    const nextLink = links.find((link) => {
        const text = stripHtml(link.label).toLowerCase();
        return text.includes('next') || text === '»';
    });

    const siblings = isMobile ? 1 : 2;
    const visibleItems = buildPaginationRange(currentPage, lastPage, siblings);

    const meta =
        from && to ? `Exibindo ${from}–${to} de ${total}` : `${total} registros`;

    function navigate(url: string | null) {
        if (!url) {
            onNavigate(null);
            return;
        }
        onNavigate(toInertiaUrl(url));
    }

    function navigateToPage(page: number) {
        const url = resolvePageUrl(page, pageLinkMap, links);
        if (url) {
            navigate(url);
        }
    }

    return (
        <footer className={cn('nx-pagination', className)}>
            <p className="nx-pagination-meta">{meta}</p>

            {lastPage > 1 ? (
                <nav className="nx-pagination-nav" aria-label={label}>
                    <button
                        type="button"
                        className="nx-page-btn"
                        disabled={!prevLink?.url}
                        onClick={() => navigate(prevLink?.url ?? null)}
                        aria-label="Página anterior"
                    >
                        <ChevronLeft size={16} strokeWidth={2} aria-hidden />
                        <span className="nx-page-btn__label">Anterior</span>
                    </button>

                    <div className="nx-pagination-pages">
                        {visibleItems.map((item) => {
                            if (item.type === 'ellipsis') {
                                return (
                                    <span
                                        key={item.key}
                                        className="nx-page-ellipsis"
                                        aria-hidden
                                    >
                                        …
                                    </span>
                                );
                            }

                            const page = item.page;
                            const pageUrl = resolvePageUrl(page, pageLinkMap, links);
                            const isActive = page === currentPage;

                            return (
                                <button
                                    key={`page-${page}`}
                                    type="button"
                                    className={cn(
                                        'nx-page-num',
                                        isActive && 'nx-page-num--active',
                                    )}
                                    disabled={!pageUrl && !isActive}
                                    onClick={() => navigateToPage(page)}
                                    aria-current={isActive ? 'page' : undefined}
                                    aria-label={`Página ${page}`}
                                >
                                    {page}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        className="nx-page-btn"
                        disabled={!nextLink?.url}
                        onClick={() => navigate(nextLink?.url ?? null)}
                        aria-label="Próxima página"
                    >
                        <span className="nx-page-btn__label">Próxima</span>
                        <ChevronRight size={16} strokeWidth={2} aria-hidden />
                    </button>
                </nav>
            ) : null}
        </footer>
    );
}

export { Pagination };
export type { PaginationProps, PaginatorLink };
